import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import cx from "classnames";
import { Primary, Secondary, SvgIcon, Text, designTokens } from "@cinch-labs/design-system";
import { useViewport, getLastCheckoutStage, returnToCheckoutStage } from "@cinch-labs/shared-util";
import { checkOrderHasExpired, getBasketBannerClosed, setBasketBannerClosed } from "../utils";
import BasketBannerVehicleInformation from "../vehicle-information/vehicle-information";
import { basketBannerCloseBannerEvent, basketBannerNoLongerWantVehicleClickEvent, basketBannerReturnToCheckoutEvent } from "./banner-analytics";
import styles from "./banner.module.css";
import { useAdobeTarget } from "@cinch-labs/adobe";
export function Banner({ activeOrder , locale ={
    orderActive: {
        buttonText: "Return to checkout",
        cancelText: "I don't want this car"
    }
} , extendOrderExpiry , refreshActiveOrder , sendDigitalDataEvent , reportDatadogError , accessToken , adminFeeFlag =false , adminFeeValueInPence , cancelOrder  }) {
    var ref;
    const basketBannerState = getBasketBannerClosed();
    const [returnToCheckoutButtonLoading, setReturnToCheckoutButtonLoading] = useState(false);
    const [hideBasketBanner, setHideBasketBanner] = useState(basketBannerState);
    const { deepPurple  } = designTokens.customProperties.ds.color;
    const dontWantCarExperiment = ((ref = useAdobeTarget("I_DONT_WANT_THIS_CAR")) === null || ref === void 0 ? void 0 : ref.ExperienceId) === "1";
    const { vw  } = useViewport({
        updateOnResize: true,
        updateOnOrientationChange: true
    });
    const isMobile = vw < 768;
    useEffect(()=>{
        checkOrderHasExpired(activeOrder, ()=>{
            setHideBasketBanner(true);
            setBasketBannerClosed(true);
        });
    }, []);
    const extendExpiry = async ()=>{
        if (activeOrder === undefined || accessToken === undefined) return;
        setReturnToCheckoutButtonLoading(true);
        try {
            await extendOrderExpiry(activeOrder.id, accessToken);
        } catch (error) {
            reportDatadogError(new Error("Extend Expiry Error"), error);
        }
        setReturnToCheckoutButtonLoading(false);
    };
    const handleCloseBanner = useCallback(()=>{
        setHideBasketBanner(true);
        setBasketBannerClosed(true);
        sendDigitalDataEvent(basketBannerCloseBannerEvent());
    }, []);
    const handleCarNoLongerWanted = ()=>{
        if (activeOrder === undefined || accessToken === undefined) return;
        setHideBasketBanner(true);
        setBasketBannerClosed(true);
        sendDigitalDataEvent(basketBannerNoLongerWantVehicleClickEvent());
        const reason = "customer no longer wants car";
        cancelOrder && cancelOrder(activeOrder.id, reason, accessToken);
    };
    const handleReturnToCheckout = useCallback(async ()=>{
        if (activeOrder === undefined) return;
        await extendExpiry();
        const lastVisitedStage = getLastCheckoutStage(activeOrder);
        await refreshActiveOrder();
        if (lastVisitedStage) {
            returnToCheckoutStage(lastVisitedStage.stage, activeOrder);
            sendDigitalDataEvent(basketBannerReturnToCheckoutEvent());
        }
    }, [
        activeOrder
    ]);
    return activeOrder ? /*#__PURE__*/ _jsx("div", {
        className: cx(styles["banner"], {
            [styles["hideBasketBanner"]]: hideBasketBanner
        }),
        children: /*#__PURE__*/ _jsx("section", {
            role: "dialog",
            "aria-labelledby": "active-order-banner",
            "aria-describedby": "description-of-active-order-vehicle",
            "aria-hidden": hideBasketBanner,
            className: styles["bannerSection"],
            children: isMobile ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        "data-testid": "mobileYourOrder",
                        className: styles["bannerHeading"],
                        id: "active-order-banner",
                        children: "Your order"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        "aria-label": "Close Active Order Banner",
                        "data-testid": "closeBanner",
                        className: styles["closeIconButton"],
                        onClick: ()=>handleCloseBanner(),
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "cross",
                            height: 12,
                            width: 12,
                            stroke: deepPurple,
                            strokeWidth: "2"
                        })
                    }),
                    /*#__PURE__*/ _jsx(BasketBannerVehicleInformation, {
                        sendDigitalDataEvent: sendDigitalDataEvent,
                        vehicle: activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle,
                        adminFeeFlag: adminFeeFlag,
                        adminFeeValueInPence: adminFeeValueInPence
                    }),
                    dontWantCarExperiment ? /*#__PURE__*/ _jsxs("div", {
                        className: styles.buttonWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Primary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: ()=>handleReturnToCheckout(),
                                size: "large",
                                children: locale.orderActive.buttonText
                            }),
                            /*#__PURE__*/ _jsx(Secondary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: ()=>handleCarNoLongerWanted(),
                                size: "large",
                                children: locale.orderActive.cancelText
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(Primary, {
                        classNames: {
                            button: styles["returnToCheckoutCta"]
                        },
                        isLoading: returnToCheckoutButtonLoading,
                        disabled: false,
                        onClick: ()=>handleReturnToCheckout(),
                        size: "large",
                        children: locale.orderActive.buttonText
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: styles["basketBannerWrapper"],
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        "aria-label": "Close Active Order Banner",
                        "data-testid": "closeBanner",
                        className: styles["closeIconButton"],
                        onClick: ()=>handleCloseBanner(),
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "cross",
                            height: 12,
                            width: 12,
                            stroke: deepPurple,
                            strokeWidth: "2"
                        })
                    }),
                    /*#__PURE__*/ _jsx(BasketBannerVehicleInformation, {
                        sendDigitalDataEvent: sendDigitalDataEvent,
                        vehicle: activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle,
                        adminFeeFlag: adminFeeFlag,
                        adminFeeValueInPence: adminFeeValueInPence
                    }),
                    dontWantCarExperiment ? /*#__PURE__*/ _jsxs("div", {
                        className: styles.buttonWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Primary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: ()=>handleReturnToCheckout(),
                                size: "large",
                                children: locale.orderActive.buttonText
                            }),
                            /*#__PURE__*/ _jsx(Secondary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: ()=>handleCarNoLongerWanted(),
                                size: "large",
                                children: locale.orderActive.cancelText
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(Primary, {
                        classNames: {
                            button: styles["returnToCheckoutCta"]
                        },
                        isLoading: returnToCheckoutButtonLoading,
                        disabled: false,
                        onClick: ()=>handleReturnToCheckout(),
                        size: "large",
                        children: locale.orderActive.buttonText
                    })
                ]
            })
        })
    }) : null;
}
export default Banner;
